import { ChevronDownIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";

import { PitchTypeTag } from "./PitchTypeTag";

interface SelectionsPanelProps {
  selectedPitches: Set<string>;
  movements: any[];
  colorPalette: string[];
  avatarOffsets: Record<string, { x: number; y: number; z: number }>;
  avatarRotations: Record<string, { x: number; y: number; z: number }>;
  onColorChangeAll: (color: string) => void;
  onOffsetChangeAll: (axis: "x" | "y" | "z", value: number) => void;
  onRotationChangeAll: (axis: "x" | "y" | "z", value: number) => void;
}

export const SelectionsPanel: React.FC<SelectionsPanelProps> = ({
  selectedPitches,
  movements,
  colorPalette,
  avatarOffsets,
  avatarRotations,
  onColorChangeAll,
  onOffsetChangeAll,
  onRotationChangeAll,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get selected movements
  const selectedMovements = movements.filter((m) => selectedPitches.has(m.id));

  // Get unique pitch types from selected movements
  const selectedPitchTypes = [
    ...new Set(selectedMovements.map((m) => m.pitchType)),
  ];

  // Get shared color if all selected movements have the same color
  const selectedColors = [...new Set(selectedMovements.map((m) => m.color))];
  const sharedColor = selectedColors.length === 1 ? selectedColors[0] : null;

  // Calculate average offset/rotation for display (use first selected movement as reference)
  const firstSelectedId = Array.from(selectedPitches)[0];
  const currentOffset = avatarOffsets[firstSelectedId] || { x: 0, y: 0, z: 0 };
  const currentRotation = avatarRotations[firstSelectedId] || {
    x: 0,
    y: 0,
    z: 0,
  };

  if (selectedPitches.size < 2) {
    return null;
  }

  return (
    <div
      className="absolute top-4 left-4 w-[318px]"
      data-testid="selections-panel"
    >
      {/* Header Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-[#595959] rounded-lg shadow-lg border border-gray-700 hover:bg-[#4D4D4D] transition-colors"
      >
        <div className="flex items-center gap-2">
          <ChevronDownIcon
            className={`w-4 h-4 text-white stroke-2 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
          <span className="text-sm font-semibold text-white">Selections</span>
        </div>
        <div className="flex items-center gap-1">
          {selectedPitchTypes.slice(0, 2).map((type) => (
            <PitchTypeTag key={type} pitchType={type} />
          ))}
          {selectedPitchTypes.length > 2 && (
            <span className="text-xs text-white ml-1">
              +{selectedPitchTypes.length - 2}
            </span>
          )}
        </div>
      </button>

      {/* Expanded Panel */}
      {isExpanded && (
        <div className="mt-2 bg-[#595959] rounded-lg shadow-lg border border-gray-700 overflow-hidden">
          {/* Pitches Section */}
          <div className="px-4 pt-4 pb-4 border-b border-gray-500">
            <h4 className="text-xs font-semibold text-white mb-2">Pitches</h4>
            <div className="flex flex-wrap gap-2">
              {selectedPitchTypes.map((type) => (
                <PitchTypeTag key={type} pitchType={type} />
              ))}
            </div>
          </div>

          {/* Color Section */}
          <div className="px-4 pt-4 pb-4 border-b border-gray-500">
            <h4 className="text-xs font-semibold text-white mb-2">Color</h4>
            <div className="grid grid-cols-10 gap-x-4 gap-y-4">
              {colorPalette.map((color) => (
                <button
                  key={color}
                  onClick={() => onColorChangeAll(color)}
                  className={`w-5 h-5 hover:scale-110 transition-all ${
                    sharedColor === color ? "border border-black scale-110" : ""
                  }`}
                  style={{ backgroundColor: color }}
                  data-testid={`color-all-${color}`}
                />
              ))}
            </div>
          </div>

          {/* Offset Section */}
          <div className="px-4 pt-4 pb-4">
            <h4 className="text-xs font-semibold text-white mb-2">Offset</h4>
            <div className="grid grid-cols-2 gap-3">
              {/* Position Controls */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <h4 className="text-xs font-medium text-white">Position:</h4>
                </div>
                {(["x", "y", "z"] as const).map((axis) => (
                  <div key={axis} className="mb-1.5">
                    <div
                      className="flex items-center gap-2"
                      style={{
                        height: "28px",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        border: "1px solid #D1D5DB",
                        background: "#FFFFFF",
                        boxShadow: "0px 1px 2px 0px #0000000D",
                      }}
                    >
                      <label className="text-sm text-gray-600 w-4 uppercase">
                        {axis}:
                      </label>
                      <input
                        type="range"
                        min="-2"
                        max="2"
                        step="0.1"
                        value={currentOffset[axis]}
                        className="appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[10px] [&::-webkit-slider-thumb]:h-[10px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#FB6A07] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-[10px] [&::-moz-range-thumb]:h-[10px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#FB6A07] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                        style={{
                          width: "50px",
                          height: "5px",
                          background: `linear-gradient(to right, #FB6A07 0%, #FB6A07 ${((currentOffset[axis] + 2) / 4) * 100}%, #D1D5DB ${((currentOffset[axis] + 2) / 4) * 100}%, #D1D5DB 100%)`,
                          borderRadius: "2.5px",
                          outline: "none",
                        }}
                        onChange={(e) =>
                          onOffsetChangeAll(axis, parseFloat(e.target.value))
                        }
                      />
                      <span className="text-sm text-gray-600 w-8 text-center">
                        {currentOffset[axis].toFixed(1)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Rotation Controls */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <h4 className="text-xs font-medium text-white">Rotation:</h4>
                </div>
                {(["x", "y", "z"] as const).map((axis) => (
                  <div key={axis} className="mb-1.5">
                    <div
                      className="flex items-center gap-2"
                      style={{
                        height: "28px",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        border: "1px solid #D1D5DB",
                        background: "#FFFFFF",
                        boxShadow: "0px 1px 2px 0px #0000000D",
                      }}
                    >
                      <label className="text-sm text-gray-600 w-4 uppercase">
                        {axis}:
                      </label>
                      <input
                        type="range"
                        min="-45"
                        max="45"
                        step="1"
                        value={currentRotation[axis]}
                        className="appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[10px] [&::-webkit-slider-thumb]:h-[10px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#FB6A07] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-[10px] [&::-moz-range-thumb]:h-[10px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#FB6A07] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                        style={{
                          width: "50px",
                          height: "5px",
                          background: `linear-gradient(to right, #FB6A07 0%, #FB6A07 ${((currentRotation[axis] + 45) / 90) * 100}%, #D1D5DB ${((currentRotation[axis] + 45) / 90) * 100}%, #D1D5DB 100%)`,
                          borderRadius: "2.5px",
                          outline: "none",
                        }}
                        onChange={(e) =>
                          onRotationChangeAll(axis, parseFloat(e.target.value))
                        }
                      />
                      <span className="text-sm text-gray-600 w-8 text-center">
                        {currentRotation[axis]}°
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
